import { BoxOfferInvitationEntity } from "@modules/boxOffer/entities/boxOfferInvitationEntity"
import {
  BoxOfferPriceType,
  type BoxOfferRule,
  BoxOfferType,
  BoxOfferValidity,
  BoxOfferWeeklyParticipationType,
  OfferAutonomousPurchase,
} from "@modules/boxOffer/entities/boxOfferTypes"
import {
  type BoxOfferDetailedDto,
  type BoxOfferDraftDto,
  type BoxOfferItemDto,
  type BoxOfferMobileDetailedDto,
  type MobileBoxProgramRefDto,
} from "@modules/boxOffer/gateway/boxOfferDto"
import { getRecurringPeriodShortLabel } from "@modules/boxOffer/utils/boxOfferUtils"
import { PriceObjectDraftEntity, PriceObjectEntity } from "@modules/common/priceObject/entities/priceObjectEntity"
import { word } from "@modules/core/utils/i18n"
import { convertImageFromDto } from "@modules/dtos/imageDto"
import { ProgramType } from "@modules/programs/types/programTypes"
import { type HUImage } from "@modules/types/imageTypes"
import dayjs from "dayjs"

export class BoxOfferItemEntity {
  id: string
  type: BoxOfferType
  name: string
  description: string | null
  price: PriceObjectEntity | null
  minimumCommitmentPeriod: string | null
  commitmentPeriodAutoRenewal: boolean | null
  recurringPeriod: string | null
  count: number | null
  validityPeriodMonths: number | null
  renewable: boolean | null
  rules: BoxOfferRule[]
  currency: string | null
  isFree: boolean
  archived: boolean
  displayOrder: number | null
  showcaseDisplay: boolean
  priceDisplay: boolean
  autonomousPurchase: OfferAutonomousPurchase
  activeMembers: number

  protected constructor(
    boxOfferItem: Omit<
      BoxOfferItemEntity,
      | "getOfferTypeLabel"
      | "getCommitmentPeriodText"
      | "getValidityPeriodText"
      | "getSessionCountText"
      | "getReservationRulesText"
    >,
  ) {
    this.id = boxOfferItem.id
    this.type = boxOfferItem.type
    this.name = boxOfferItem.name
    this.description = boxOfferItem.description
    this.price = boxOfferItem.price
    this.minimumCommitmentPeriod = boxOfferItem.minimumCommitmentPeriod
    this.count = boxOfferItem.count
    this.validityPeriodMonths = boxOfferItem.validityPeriodMonths
    this.renewable = boxOfferItem.renewable
    this.rules = boxOfferItem.rules
    this.currency = boxOfferItem.currency
    this.isFree = boxOfferItem.isFree
    this.archived = boxOfferItem.archived
    this.commitmentPeriodAutoRenewal = boxOfferItem.commitmentPeriodAutoRenewal
    this.recurringPeriod = boxOfferItem.recurringPeriod
    this.displayOrder = boxOfferItem.displayOrder
    this.showcaseDisplay = boxOfferItem.showcaseDisplay
    this.priceDisplay = boxOfferItem.priceDisplay
    this.autonomousPurchase = boxOfferItem.autonomousPurchase
    this.activeMembers = boxOfferItem.activeMembers
  }

  static fromItemDto(dto: BoxOfferItemDto): BoxOfferItemEntity {
    const entity = new BoxOfferItemEntity({
      ...dto,
      price: dto.priceObject ? PriceObjectEntity.fromDto(dto.priceObject) : null,
    })

    return entity
  }

  getOfferTypeLabel(): string {
    switch (this.type) {
      case BoxOfferType.Subscription:
        return word("box.offer.form.types.SUBSCRIPTION")
      case BoxOfferType.DropIn:
        return word("box.offer.form.types.DROP_IN")
      case BoxOfferType.Trial:
        return word("box.offer.form.types.TRIAL")
      case BoxOfferType.TicketsBook:
        return word("box.offer.form.types.TICKETS_BOOK")
      case BoxOfferType.Millenium:
        return word("box.offer.form.types.MILLENIUM")
      default:
        return ""
    }
  }

  getCommitmentPeriodText(): string | null {
    if (this.type !== BoxOfferType.Subscription) return null

    if (this.minimumCommitmentPeriod) {
      // Parse ISO duration string (e.g., "P12M" for 12 months)
      const months = dayjs.duration(this.minimumCommitmentPeriod).asMonths()
      if (months > 0) {
        if (this.commitmentPeriodAutoRenewal) {
          return `${word("box.offer.form.infos.commitment", { count: months })} · ${word("box.offer.form.infos.commitmentPeriodAutoRenewal.active")}`
        }

        return word("box.offer.form.infos.commitment", { count: months })
      }
    }

    return null
  }

  getValidityPeriodText(): string | null {
    if (![BoxOfferType.DropIn, BoxOfferType.Trial, BoxOfferType.TicketsBook].includes(this.type)) {
      return null
    }

    if (this.validityPeriodMonths) {
      return word("box.offer.form.infos.validity", { count: this.validityPeriodMonths })
    }

    return null
  }

  getSessionCountText(): string | null {
    if (![BoxOfferType.DropIn, BoxOfferType.Trial, BoxOfferType.TicketsBook].includes(this.type)) {
      return null
    }

    const count = this.count ?? 1

    return word("box.offer.type.per.session", { count })
  }

  getReservationRulesText(): string[] {
    if (this.rules.length === 0) return []

    return this.rules.map((rule) => {
      const periodMap = {
        DAY: word("global.day.label", { count: 1 }),
        WEEK: word("global.week.label", { count: 1 }),
        MONTH: word("global.month.label", { count: 1 }),
      }
      const period = periodMap[rule.period]
      return `${String(rule.value)}x/${period}`
    })
  }
}

export type BoxOfferDetailedState = Omit<BoxOfferDetailedEntity, "setArchived">

export class BoxOfferDetailedEntity extends BoxOfferItemEntity {
  id: string
  type: BoxOfferType
  name: string
  description: string | null
  price: PriceObjectEntity | null
  minimumCommitmentPeriod: string | null
  commitmentPeriodAutoRenewal: boolean | null
  recurringPeriod: string | null
  count: number | null
  maxWeeklyParticipation: number | null
  validityPeriodMonths: number | null
  renewable: boolean | null
  showcaseDisplay: boolean
  priceDisplay: boolean
  archived: boolean
  programs: string[]
  rules: BoxOfferRule[]
  minimumMonthlyDuration?: number
  /**
   * Deprecated
   */
  currency: string | null
  isFree: boolean

  private constructor(
    boxOffer: Omit<
      BoxOfferDetailedEntity,
      | "getOfferTypeLabel"
      | "getCommitmentPeriodText"
      | "getValidityPeriodText"
      | "getSessionCountText"
      | "getReservationRulesText"
      | "setArchived"
    >,
  ) {
    super(boxOffer)
    this.id = boxOffer.id
    this.type = boxOffer.type
    this.name = boxOffer.name
    this.description = boxOffer.description
    this.price = boxOffer.price
    this.minimumCommitmentPeriod = boxOffer.minimumCommitmentPeriod
    this.commitmentPeriodAutoRenewal = boxOffer.commitmentPeriodAutoRenewal
    this.recurringPeriod = boxOffer.recurringPeriod
    this.count = boxOffer.count
    this.maxWeeklyParticipation = boxOffer.maxWeeklyParticipation
    this.validityPeriodMonths = boxOffer.validityPeriodMonths
    this.renewable = boxOffer.renewable
    this.showcaseDisplay = boxOffer.showcaseDisplay
    this.priceDisplay = boxOffer.priceDisplay
    this.archived = boxOffer.archived
    this.programs = boxOffer.programs
    this.rules = boxOffer.rules
    this.currency = boxOffer.currency
    this.isFree = boxOffer.isFree
    this.minimumMonthlyDuration = boxOffer.minimumMonthlyDuration
  }

  static fromDto(dto: BoxOfferDetailedDto): BoxOfferDetailedEntity {
    const offer = new BoxOfferDetailedEntity({
      ...dto,
      price: dto.priceObject ? PriceObjectEntity.fromDto(dto.priceObject) : null,
      activeMembers: 0, // workaround to avoid breaking changes on BoxOfferItemEntity which is used in many places, will be properly set in BoxOfferMobileDetailedEntity.fromDto
    })

    switch (dto.type) {
      case BoxOfferType.Subscription:
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        offer.minimumMonthlyDuration = dayjs.duration(dto.minimumCommitmentPeriod!).months()
        offer.commitmentPeriodAutoRenewal = !!dto.commitmentPeriodAutoRenewal
        break
      case BoxOfferType.Trial:
        offer.count = offer.count ?? 0
        offer.renewable = !!offer.renewable
        break
      case BoxOfferType.TicketsBook:
        offer.count = offer.count ?? 0
        break
      case BoxOfferType.Millenium:
        offer.isFree = true
        offer.price = null
        break
    }

    return offer
  }

  setArchived(value: boolean) {
    this.archived = value
  }
}

export type BoxOfferDraftState = Omit<BoxOfferDraftEntity, "setArchived">

export class BoxOfferDraftEntity {
  type: BoxOfferType
  name: string
  description: string
  priceSwitch: BoxOfferPriceType
  price: number | null
  currency?: string
  weeklyParticipationSwitch: BoxOfferWeeklyParticipationType
  maxWeeklyParticipation?: number
  minimumMonthlyDuration?: number
  commitmentPeriodAutoRenewal?: boolean
  recurringPeriod?: string
  programs: string[]
  rules?: BoxOfferRule[]
  renewable?: boolean
  validityPeriodMonths?: number
  validitySwitch: BoxOfferValidity
  showcaseDisplay: boolean
  priceDisplay: boolean
  autonomousPurchase: OfferAutonomousPurchase
  archived?: boolean
  /**
   * Deprecated
   */
  count?: number

  private constructor(boxOfferDraft: BoxOfferDraftState) {
    this.type = boxOfferDraft.type
    this.name = boxOfferDraft.name
    this.description = boxOfferDraft.description
    this.priceSwitch = boxOfferDraft.priceSwitch
    this.price = boxOfferDraft.price
    this.currency = boxOfferDraft.currency
    this.weeklyParticipationSwitch = boxOfferDraft.weeklyParticipationSwitch
    this.maxWeeklyParticipation = boxOfferDraft.maxWeeklyParticipation
    this.minimumMonthlyDuration = boxOfferDraft.minimumMonthlyDuration
    this.commitmentPeriodAutoRenewal = boxOfferDraft.commitmentPeriodAutoRenewal
    this.recurringPeriod = boxOfferDraft.recurringPeriod
    this.programs = boxOfferDraft.programs
    this.rules = boxOfferDraft.rules
    this.renewable = boxOfferDraft.renewable
    this.validityPeriodMonths = boxOfferDraft.validityPeriodMonths
    this.validitySwitch = boxOfferDraft.validitySwitch
    this.showcaseDisplay = boxOfferDraft.showcaseDisplay
    this.priceDisplay = boxOfferDraft.priceDisplay
    this.autonomousPurchase = boxOfferDraft.autonomousPurchase
    this.archived = boxOfferDraft.archived
    this.count = boxOfferDraft.count
  }

  static toDraft(
    boxOfferDraft?: BoxOfferDetailedEntity,
    selectedBoxOfferType?: BoxOfferType,
    defaultCurrency?: string,
  ): BoxOfferDraftEntity {
    return new BoxOfferDraftEntity({
      type: boxOfferDraft?.type ?? selectedBoxOfferType ?? BoxOfferType.Subscription,
      name: boxOfferDraft?.name ?? "",
      description: boxOfferDraft?.description ?? "",
      priceSwitch:
        boxOfferDraft?.isFree ||
        (selectedBoxOfferType && [BoxOfferType.Trial, BoxOfferType.Millenium].includes(selectedBoxOfferType))
          ? BoxOfferPriceType.Free
          : BoxOfferPriceType.Paid,
      price: boxOfferDraft?.price?.value ?? null,
      currency: boxOfferDraft?.price?.currency ?? defaultCurrency ?? "EUR",
      programs: boxOfferDraft?.programs ?? [],
      count:
        boxOfferDraft?.type === BoxOfferType.TicketsBook || boxOfferDraft?.type === BoxOfferType.Trial
          ? (boxOfferDraft.count ?? undefined)
          : undefined,
      minimumMonthlyDuration:
        boxOfferDraft?.type === BoxOfferType.Subscription
          ? (boxOfferDraft.minimumMonthlyDuration ?? undefined)
          : selectedBoxOfferType === BoxOfferType.Subscription
            ? 1
            : undefined,
      commitmentPeriodAutoRenewal: boxOfferDraft?.commitmentPeriodAutoRenewal ?? false,
      recurringPeriod:
        boxOfferDraft?.type === BoxOfferType.Subscription
          ? (boxOfferDraft.recurringPeriod ?? "P1M")
          : selectedBoxOfferType === BoxOfferType.Subscription
            ? "P1M"
            : undefined,
      weeklyParticipationSwitch: boxOfferDraft?.maxWeeklyParticipation
        ? BoxOfferWeeklyParticipationType.Restricted
        : BoxOfferWeeklyParticipationType.Unlimited,
      maxWeeklyParticipation: boxOfferDraft?.maxWeeklyParticipation ?? undefined,
      rules: boxOfferDraft?.rules ?? [],
      renewable:
        boxOfferDraft?.type === BoxOfferType.Trial
          ? (boxOfferDraft.renewable ?? undefined)
          : selectedBoxOfferType === BoxOfferType.Trial
            ? false
            : undefined,
      validityPeriodMonths: boxOfferDraft?.validityPeriodMonths ?? undefined,
      validitySwitch: boxOfferDraft?.validityPeriodMonths ? BoxOfferValidity.DEFINED : BoxOfferValidity.UNLIMITED,
      showcaseDisplay: boxOfferDraft?.showcaseDisplay ?? true,
      priceDisplay: boxOfferDraft?.priceDisplay ?? true,
      autonomousPurchase: boxOfferDraft?.autonomousPurchase ?? OfferAutonomousPurchase.PUBLIC,
      archived: boxOfferDraft?.archived ?? false,
    })
  }

  static fromState(state: BoxOfferDraftState): BoxOfferDraftEntity {
    return new BoxOfferDraftEntity(state)
  }

  static toDto(draft: BoxOfferDraftEntity): BoxOfferDraftDto {
    return {
      type: draft.type,
      name: draft.name,
      description: draft.description,
      priceObject:
        draft.priceSwitch === BoxOfferPriceType.Paid
          ? PriceObjectDraftEntity.toDto({ value: draft.price ?? 0, currency: draft.currency })
          : undefined,
      programs: draft.programs,
      count:
        (draft.type === BoxOfferType.TicketsBook || draft.type === BoxOfferType.Trial) && draft.count
          ? draft.count
          : undefined,
      minimumCommitmentPeriod:
        draft.type === BoxOfferType.Subscription
          ? dayjs.duration({ months: draft.minimumMonthlyDuration }).toISOString()
          : undefined,
      maxWeeklyParticipation:
        draft.weeklyParticipationSwitch === BoxOfferWeeklyParticipationType.Restricted
          ? (draft.maxWeeklyParticipation ?? null)
          : null,
      commitmentPeriodAutoRenewal: draft.commitmentPeriodAutoRenewal,
      recurringPeriod: draft.type === BoxOfferType.Subscription ? draft.recurringPeriod : undefined,
      rules: draft.rules,
      renewable: draft.renewable,
      validityPeriodMonths: draft.validityPeriodMonths,
      showcaseDisplay: draft.showcaseDisplay,
      priceDisplay: draft.priceDisplay,
      autonomousPurchase: draft.autonomousPurchase,
      archived: draft.archived ?? false,
    }
  }

  setArchived(value: boolean) {
    this.archived = value
  }
}

export class MobileBoxProgramRef {
  id: string
  name: string
  image: HUImage | null
  type: ProgramType

  private constructor(program: MobileBoxProgramRef) {
    this.id = program.id
    this.name = program.name
    this.image = program.image
    this.type = program.type
  }

  static fromDto(dto: MobileBoxProgramRefDto): MobileBoxProgramRef {
    return new MobileBoxProgramRef({
      ...dto,
      image: dto.image ? convertImageFromDto(dto.image) : null,
    })
  }
}

export type BoxOfferMobileDetailedState = Omit<BoxOfferMobileDetailedEntity, "getBoxOfferTypeWord">
export class BoxOfferMobileDetailedEntity {
  id: string
  type: BoxOfferType
  name: string
  description: string
  priceObject: PriceObjectEntity | null
  minimumCommitmentPeriod: string | null
  commitmentPeriodAutoRenewal: boolean | null
  recurringPeriod: string | null
  count: number | null
  maxWeeklyParticipation: number | null
  validityPeriodMonths: number | null
  renewable: boolean | null
  showcaseDisplay: boolean
  priceDisplay: boolean
  autonomousPurchase: OfferAutonomousPurchase
  canUserPurchase: boolean
  programs: MobileBoxProgramRef[]
  rules: BoxOfferRule[]
  currency: string | null
  hasAlreadyPurchase: boolean | null
  invitation: BoxOfferInvitationEntity | null

  private constructor(boxOffer: BoxOfferMobileDetailedState) {
    this.id = boxOffer.id
    this.type = boxOffer.type
    this.name = boxOffer.name
    this.description = boxOffer.description
    this.priceObject = boxOffer.priceObject
    this.minimumCommitmentPeriod = boxOffer.minimumCommitmentPeriod
    this.commitmentPeriodAutoRenewal = boxOffer.commitmentPeriodAutoRenewal
    this.recurringPeriod = boxOffer.recurringPeriod
    this.count = boxOffer.count
    this.maxWeeklyParticipation = boxOffer.maxWeeklyParticipation
    this.validityPeriodMonths = boxOffer.validityPeriodMonths
    this.renewable = boxOffer.renewable
    this.showcaseDisplay = boxOffer.showcaseDisplay
    this.priceDisplay = boxOffer.priceDisplay
    this.autonomousPurchase = boxOffer.autonomousPurchase
    this.canUserPurchase = boxOffer.canUserPurchase
    this.programs = boxOffer.programs
    this.rules = boxOffer.rules
    this.currency = boxOffer.currency
    this.hasAlreadyPurchase = boxOffer.hasAlreadyPurchase
    this.invitation = boxOffer.invitation
  }

  static fromDto(dto: BoxOfferMobileDetailedDto): BoxOfferMobileDetailedEntity {
    return new BoxOfferMobileDetailedEntity({
      ...dto,
      commitmentPeriodAutoRenewal: dto.commitmentPeriodAutoRenewal ?? false,
      minimumCommitmentPeriod: dto.minimumCommitmentPeriod
        ? dayjs.duration(dto.minimumCommitmentPeriod).months().toString()
        : null,
      priceObject: dto.priceObject ? PriceObjectEntity.fromDto(dto.priceObject) : null,
      programs: dto.programs.map((program) => MobileBoxProgramRef.fromDto(program)),
      description: dto.description ?? "",
      invitation: dto.invitation ? BoxOfferInvitationEntity.fromDto(dto.invitation) : null,
    })
  }

  getBoxOfferTypeWord = () => {
    switch (this.type) {
      case BoxOfferType.Subscription:
        return getRecurringPeriodShortLabel(this.recurringPeriod)
      case BoxOfferType.DropIn:
      case BoxOfferType.Trial:
      case BoxOfferType.TicketsBook:
        return word("box.offer.type.per.session", { count: this.count ?? 1 })
      case BoxOfferType.Millenium:
        return ""
    }
  }
}

export type BoxOfferMobileItemState = Omit<BoxOfferMobileItemEntity, "getBoxOfferTypeWord">

export class BoxOfferMobileItemEntity {
  id: string
  type: BoxOfferType
  name: string
  description: string
  priceObject: PriceObjectEntity | null
  minimumCommitmentPeriod: string | null
  commitmentPeriodAutoRenewal: boolean | null
  recurringPeriod: string | null
  count?: number | null
  validityPeriodMonths: number | null
  renewable: boolean | null
  showcaseDisplay: boolean
  priceDisplay: boolean
  autonomousPurchase: OfferAutonomousPurchase
  canUserPurchase: boolean
  rules: BoxOfferRule[]
  currency: string | null

  private constructor(boxOffer: BoxOfferMobileItemState) {
    this.id = boxOffer.id
    this.type = boxOffer.type
    this.name = boxOffer.name
    this.description = boxOffer.description
    this.priceObject = boxOffer.priceObject
    this.minimumCommitmentPeriod = boxOffer.minimumCommitmentPeriod
    this.commitmentPeriodAutoRenewal = boxOffer.commitmentPeriodAutoRenewal
    this.recurringPeriod = boxOffer.recurringPeriod
    this.count = boxOffer.count
    this.validityPeriodMonths = boxOffer.validityPeriodMonths
    this.renewable = boxOffer.renewable
    this.showcaseDisplay = boxOffer.showcaseDisplay
    this.priceDisplay = boxOffer.priceDisplay
    this.autonomousPurchase = boxOffer.autonomousPurchase
    this.canUserPurchase = boxOffer.canUserPurchase
    this.rules = boxOffer.rules
    this.currency = boxOffer.currency
  }

  getBoxOfferTypeWord = () => {
    switch (this.type) {
      case BoxOfferType.Subscription:
        return getRecurringPeriodShortLabel(this.recurringPeriod)
      case BoxOfferType.DropIn:
      case BoxOfferType.Trial:
      case BoxOfferType.TicketsBook:
        return word("box.offer.type.per.session", { count: this.count ?? 1 })
      case BoxOfferType.Millenium:
        return ""
    }
  }

  static fromDto(dto: BoxOfferMobileItemState): BoxOfferMobileItemEntity {
    return new BoxOfferMobileItemEntity({
      ...dto,
      priceObject: dto.priceObject ? PriceObjectEntity.fromDto(dto.priceObject) : null,
      minimumCommitmentPeriod: dto.minimumCommitmentPeriod
        ? dayjs.duration(dto.minimumCommitmentPeriod).months().toString()
        : null,
      description: dto.description,
    })
  }
}
